import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography, Chip, Paper, Divider, Button, TextField, IconButton} from 'views/components';
import {DeleteForever, CloudUpload} from 'views/icons';
import {Translate, translate, DatePickerYearMonth, UploadFileToS3, SecureFileGrid} from 'views/module';

import moment from 'moment';

const CertificatesEdit = (props) => {
  const DEFAULT_CERTIFICATION_DETAIL = {
    type: '',
    issuingOrganization: '',
    city: '',
    dateObtained: '',
    attachments: [],
  };
  const {bioQuestions, setFields, setIsComplete} = props;

  const [certificates, setCertificates] = React.useState([]);
  const [subsectionState, setSubsectionState] = React.useState('CHIP');
  const [certificationDetails, setCertificationDetails] = React.useState({...DEFAULT_CERTIFICATION_DETAIL});

  // Loads the certifications
  React.useEffect(() => {
    setCertificates(bioQuestions?.certificates || []);
  }, [bioQuestions]);

  // Sets if it is complete
  React.useEffect(() => {
    setIsComplete(certificates.length > 0 && subsectionState === 'CHIP');
  }, [certificates, subsectionState]);

  const resetFormState = () => {
    setSubsectionState('CHIP');
    setCertificationDetails({...DEFAULT_CERTIFICATION_DETAIL});
  };

  const updateCertificates = (updater) => {
    setCertificates((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setFields((prevFields) => ({...prevFields, certificates: next}));
      return next;
    });
  };

  const openCertificateForm = (key) => {
    const existing = certificates.find((c) => c.type === key);
    setCertificationDetails(existing || {...DEFAULT_CERTIFICATION_DETAIL, type: key});
    setSubsectionState('FORM');
  };

  const handleRemoveCertificate = (key) => {
    updateCertificates((prev) => prev.filter((c) => c.type !== key));
    if (certificationDetails.type === key) resetFormState();
  };

  const handleSaveCertificate = () => {
    if (!certificationDetails.type) return;
    updateCertificates((prev) => {
      const filtered = prev.filter((c) => c.type !== certificationDetails.type);
      return [...filtered, certificationDetails];
    });
    resetFormState();
  };

  const handleRemoveAttachment = (fileToRemove) => {
    setCertificationDetails((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((file) => file.src !== fileToRemove.src),
    }));
  };

  const handleUploadAttachment = async (file) => {
    const onComplete = (fileUrl) => {
      setCertificationDetails((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), {name: file.name, type: file.type, src: fileUrl}],
      }));
    };
    return props.getSignedFileUrl('certificates', file, false, onComplete);
  };

  const availableCertifications = translate('caregiver.CaregiverBiography.Certificate.AvailableCertifications') || {};
  const certificationKeys = Object.keys(availableCertifications);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5} alignItems="center" textAlign="center">
        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
          <Translate expression="caregiver.CaregiverBiography.Certificate.Title" />
        </Typography>
      </Stack>

      {
        subsectionState === 'CHIP' &&
        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={1}>
          {
            certificationKeys.map((key) => {
              const isSelected = certificates.some((c) => c.type === key);
              return (
                <Chip
                  key={key}
                  label={translate(`caregiver.CaregiverBiography.Certificate.AvailableCertifications.${key}`)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => openCertificateForm(key)}
                  sx={{fontWeight: isSelected ? 600 : 500}}
                />
              );
            })
          }
        </Box>
      }

      {
        subsectionState === 'FORM' &&
        <Paper sx={{p: 3, borderRadius: 2}}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
              <Translate expression={`caregiver.CaregiverBiography.Certificate.AvailableCertifications.${certificationDetails.type}`} />
            </Typography>
            <Divider />

            <TextField
              label={translate('caregiver.CaregiverBiography.Certificate.IssuingOrgOrSchool.Label')}
              placeholder={translate('caregiver.CaregiverBiography.Certificate.IssuingOrgOrSchool.Placeholder')}
              value={certificationDetails.issuingOrganization}
              onChange={(v) => setCertificationDetails((prev) => ({...prev, issuingOrganization: v}))}
              fullWidth
            />

            <TextField
              label={translate('caregiver.CaregiverBiography.Certificate.City.Label')}
              placeholder={translate('caregiver.CaregiverBiography.Certificate.City.Placeholder')}
              value={certificationDetails.city}
              onChange={(v) => setCertificationDetails((prev) => ({...prev, city: v}))}
              fullWidth
            />

            <DatePickerYearMonth
              label={translate('caregiver.CaregiverBiography.Certificate.DateObtained.Label')}
              format="MMM YYYY"
              value={certificationDetails.dateObtained ? moment(certificationDetails.dateObtained) : null}
              onChange={(date) => setCertificationDetails((prev) => ({...prev, dateObtained: moment(date).toISOString()}))}
              textFieldProps={{
                fullWidth: true,
                placeholder: translate('caregiver.CaregiverBiography.Certificate.DateObtained.Placeholder'),
              }}
            />

            <Stack spacing={1.5}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  <Translate expression="caregiver.CaregiverBiography.VulnerableSectorCheck.Documents" />
                </Typography>
                {
                  certificationDetails.attachments?.length ?
                    <SecureFileGrid
                      files={certificationDetails.attachments}
                      menuItems={[
                        {
                          Icon: DeleteForever,
                          text: translate('generic.Delete'),
                          handler: handleRemoveAttachment,
                        },
                      ]}
                    /> :
                    <Typography variant="body2" color="text.secondary">
                      <Translate expression="caregiver.CaregiverBiography.VulnerableSectorCheck.EmptyState" />
                    </Typography>
                }
              </Stack>

              <UploadFileToS3 getSignedUrl={handleUploadAttachment}>
                <Box
                  sx={(theme) => ({
                    'width': '100%',
                    'minHeight': 180,
                    'borderRadius': '16px',
                    'cursor': 'pointer',
                    'display': 'flex',
                    'alignItems': 'center',
                    'justifyContent': 'center',
                    'padding': theme.spacing(3),
                    'border': `2px dashed ${
                      theme.palette.mode === 'light' ?
                        'rgba(0, 0, 0, 0.12)' :
                        'rgba(255, 255, 255, 0.25)'
                    }`,
                    'background':
                      theme.palette.mode === 'light' ?
                        'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 250, 0.95))' :
                        theme.palette.background.paper,
                    'boxShadow':
                      theme.palette.mode === 'light' ?
                        '0 12px 24px rgba(31, 41, 55, 0.08)' :
                        '0 16px 32px rgba(0, 0, 0, 0.45)',
                    'transition': 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      boxShadow:
                        theme.palette.mode === 'light' ?
                          '0 18px 38px rgba(31, 41, 55, 0.12)' :
                          '0 20px 40px rgba(0, 0, 0, 0.55)',
                      transform: 'translateY(-2px)',
                      background:
                        theme.palette.mode === 'light' ?
                          'linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(245, 247, 250, 1))' :
                          theme.palette.background.default,
                    },
                    '&:focus-within': {
                      borderColor: theme.palette.primary.main,
                      boxShadow:
                        theme.palette.mode === 'light' ?
                          `0 0 0 4px ${theme.palette.primary.main}33` :
                          `0 0 0 4px ${theme.palette.primary.main}4d`,
                    },
                  })}
                >
                  <Stack spacing={1.25} alignItems="center" textAlign="center">
                    <Box
                      sx={(theme) => ({
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor:
                          theme.palette.mode === 'light' ?
                            `${theme.palette.primary.main}14` :
                            `${theme.palette.primary.main}26`,
                        color: theme.palette.primary.main,
                      })}
                    >
                      <CloudUpload fontSize="medium" />
                    </Box>
                    <Typography variant="subtitle1" color="text.primary" sx={{fontWeight: 600}}>
                      <Translate expression="caregiver.CaregiverBiography.VulnerableSectorCheck.UploadTitle" />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Translate expression="caregiver.CaregiverBiography.VulnerableSectorCheck.UploadDescription" />
                    </Typography>
                  </Stack>
                </Box>
              </UploadFileToS3>
            </Stack>

            <Divider />

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} justifyContent="space-between">
              <Button
                color="error"
                variant="outlined"
                onClick={() => handleRemoveCertificate(certificationDetails.type)}
                label={translate('generic.Delete')}
                sx={{minWidth: 140}}
              />
              <Button
                color="primary"
                onClick={handleSaveCertificate}
                label={translate('generic.Save')}
                sx={{minWidth: 140}}
              />
            </Stack>
          </Stack>
        </Paper>
      }

      {
        certificates.length > 0 &&
        <Paper sx={{p: 2.5, borderRadius: 2}}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
              <Translate
                expression={`caregiver.CaregiverBiography.Certificate.SelectedCount`}
                substitute={{count: certificates.length}}
              />
            </Typography>

            <Box display="flex" flexDirection="column" gap={1}>
              {
                certificates.map((certificate) => {
                  const label = translate(`caregiver.CaregiverBiography.Certificate.AvailableCertifications.${certificate.type}`);
                  const date = moment(certificate.dateObtained).isValid() ?
                  moment(certificate.dateObtained).format('YYYY') :
                  null;

                  const details = [label, certificate.issuingOrganization, date, certificate.city]
                      .filter(Boolean)
                      .join(' \u2022 ');
                  const attachments = certificate.attachments || [];

                  return (
                    <Box
                      key={`${certificate.type}-${certificate.issuingOrganization}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        border: '1px solid #0288d1',
                        borderRadius: '12px',
                        backgroundColor: '#e1f5fe',
                        paddingY: 1,
                        paddingX: 1.5,
                        gap: 1,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Stack spacing={0.75} sx={{flex: 1}}>
                        <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                          {details}
                        </Typography>
                        {
                          attachments.length > 0 &&
                          <SecureFileGrid files={attachments} />
                        }
                      </Stack>
                      <IconButton size="small" color="primary" onClick={() => handleRemoveCertificate(certificate.type)}>
                        <DeleteForever fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })
              }
            </Box>
          </Stack>
        </Paper>
      }
    </Stack>
  );
};

CertificatesEdit.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  setFields: PropTypes.func.isRequired,
  setIsComplete: PropTypes.func,
  getSignedFileUrl: PropTypes.func,
};

export default React.memo(CertificatesEdit);
